import { useState } from "react";
import { api, type Named, type Paged, type Role, type User } from "../lib/api.js";
import { dateTime, relativeTime } from "../lib/format.js";
import { Drawer, Empty, Notice, useAsync } from "../components/ui.js";
import type { PageProps } from "./Shell.js";

export default function Users({ me, onToggleRail }: PageProps) {
  const [editing, setEditing] = useState<User | "new" | null>(null);
  const users = useAsync(() => api.get<Paged<User>>("/api/console/users?pageSize=500"), []);
  const canWrite = me.permissions.includes("*") || me.permissions.includes("user:write");

  return (
    <>
      <header className="topbar">
        <button className="btn btn--ghost btn--sm" onClick={onToggleRail} aria-label="Menu">&#9776;</button>
        <div className="topbar__title">
          <h1>People</h1>
          <span className="topbar__hint">{users.data?.total ?? 0} accounts</span>
        </div>
        <div className="topbar__actions">
          {canWrite ? (
            <button className="btn btn--primary btn--sm" onClick={() => setEditing("new")}>
              Add person
            </button>
          ) : null}
        </div>
      </header>

      <div className="content content--flush">
        {users.error ? <div style={{ padding: "1.25rem" }}><Notice>{users.error}</Notice></div> : null}
        {!users.loading && !users.data?.data.length ? (
          <Empty title="No accounts yet" hint="Add a person so they can sign in and reach machines." />
        ) : (
          <div className="tablewrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Name</th><th>Role</th><th>Group</th><th>Sign-in</th>
                  <th>Two-factor</th><th>Last active</th><th style={{ width: 80 }} />
                </tr>
              </thead>
              <tbody>
                {(users.data?.data ?? []).map((u) => (
                  <tr key={u.id}>
                    <td>
                      <div className="row" style={{ gap: "0.5rem" }}>
                        <span className={`dot${u.status === 1 ? " dot--live" : " dot--deny"}`} />
                        <div style={{ minWidth: 0 }}>
                          <div className="truncate">{u.display_name || u.name}</div>
                          <div className="small faint">@{u.name}{u.email ? ` · ${u.email}` : ""}</div>
                        </div>
                      </div>
                    </td>
                    <td className="muted">
                      {u.is_admin ? <span className="tag tag--live">Administrator</span> : u.role_name || <span className="faint">&mdash;</span>}
                    </td>
                    <td className="muted">{u.group_name || <span className="faint">&mdash;</span>}</td>
                    <td className="muted small">{u.third_auth_type || "Password"}</td>
                    <td className="muted small">{u.tfa_enabled ? "On" : <span className="faint">Off</span>}</td>
                    <td className="muted small">{u.last_login_at ? relativeTime(u.last_login_at) : <span className="faint">Never</span>}</td>
                    <td className="actions">
                      {canWrite ? <button className="btn btn--sm" onClick={() => setEditing(u)}>Edit</button> : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing ? (
        <UserDrawer
          user={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { users.reload(); setEditing(null); }}
        />
      ) : null}
    </>
  );
}

function UserDrawer({
  user, onClose, onSaved,
}: { user: User | null; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(user?.name ?? "");
  const [displayName, setDisplayName] = useState(user?.display_name ?? "");
  const [email, setEmail] = useState(user?.email ?? "");
  const [password, setPassword] = useState("");
  const [roleId, setRoleId] = useState(user?.role_id ?? "");
  const [groupId, setGroupId] = useState(user?.group_id ?? "");
  const [isAdmin, setIsAdmin] = useState(user?.is_admin ?? false);
  const [enabled, setEnabled] = useState((user?.status ?? 1) === 1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [generated, setGenerated] = useState("");

  const roles = useAsync(() => api.get<Role[]>("/api/console/roles"), []);
  const groups = useAsync(() => api.get<Named[]>("/api/console/groups"), []);

  async function save() {
    setBusy(true);
    setError("");
    try {
      if (user) {
        await api.patch(`/api/console/users/${user.id}`, {
          display_name: displayName, email, role_id: roleId || null,
          group_id: groupId || null, is_admin: isAdmin, status: enabled ? 1 : 0,
          ...(password ? { password } : {}),
        });
        onSaved();
      } else {
        const res = await api.post<{ id: string; password?: string }>("/api/console/users", {
          name, display_name: displayName, email, password: password || undefined,
          role_id: roleId || null, group_id: groupId || null, is_admin: isAdmin,
        });
        if (res.password) {
          // Shown once: it is stored only as a hash.
          setGenerated(res.password);
          return;
        }
        onSaved();
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!user) return;
    setBusy(true);
    try {
      await api.del(`/api/console/users/${user.id}`);
      onSaved();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  }

  if (generated) {
    return (
      <Drawer title="Account created" onClose={onSaved}
        footer={<button className="btn btn--primary" onClick={onSaved}>Done</button>}>
        <p>Give {name} this password. It is not stored and cannot be shown again.</p>
        <div className="panel"><div className="panel__body">
          <code className="mono" style={{ fontSize: 16 }}>{generated}</code>
        </div></div>
      </Drawer>
    );
  }

  return (
    <Drawer
      title={user ? `Edit ${user.name}` : "Add person"}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn--primary" onClick={save} disabled={busy || (!user && !name)}>
            {busy ? "Saving…" : user ? "Save changes" : "Create account"}
          </button>
          <div className="spacer" />
          {user ? <button className="btn btn--danger btn--sm" onClick={remove} disabled={busy}>Delete</button> : null}
        </>
      }
    >
      <div className="stack">
        {error ? <Notice>{error}</Notice> : null}
        {!user ? (
          <div className="field">
            <label className="field__label">Username</label>
            <input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
            <span className="field__hint">Used to sign in. Letters, numbers, dot, dash, underscore.</span>
          </div>
        ) : null}
        <div className="field">
          <label className="field__label">Display name</label>
          <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
        </div>
        <div className="field">
          <label className="field__label">Email</label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="field">
          <label className="field__label">{user ? "New password" : "Password"}</label>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder={user ? "Leave blank to keep the current one" : "Leave blank to generate one"} />
          {user ? <span className="field__hint">Changing this signs the person out everywhere.</span> : null}
        </div>
        <div className="field">
          <label className="field__label">Role</label>
          <select value={roleId} onChange={(e) => setRoleId(e.target.value)}>
            <option value="">No role</option>
            {(roles.data ?? []).map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
          <span className="field__hint">Roles decide what this person can do in the console.</span>
        </div>
        <div className="field">
          <label className="field__label">Group</label>
          <select value={groupId} onChange={(e) => setGroupId(e.target.value)}>
            <option value="">No group</option>
            {(groups.data ?? []).map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
          <span className="field__hint">Groups decide which machines and people they can see.</span>
        </div>
        <div>
          <label className="check">
            <input type="checkbox" checked={isAdmin} onChange={(e) => setIsAdmin(e.target.checked)} />
            Administrator (full access, bypasses all permission rules)
          </label>
          <label className="check">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            Account is active
          </label>
        </div>
        {user ? (
          <dl className="kv">
            <dt>Created</dt><dd>{dateTime(user.created_at)}</dd>
            <dt>Last signed in</dt><dd>{user.last_login_at ? dateTime(user.last_login_at) : "Never"}</dd>
          </dl>
        ) : null}
      </div>
    </Drawer>
  );
}
